import { Link } from "react-router-dom";
import { ArrowRight, Lightning } from "@phosphor-icons/react";
import { BENEFITS, PROBLEMS, SOLUTIONS, STEPS, TRUST_POINTS } from "./data";

export function PricingTrustStrip() {
  return (
    <section
      aria-label="Pricing at a glance"
      data-testid="pricing-trust-strip"
      className="border-b border-white/10 bg-gradient-to-r from-black via-[#FF5A1F]/[0.03] to-black"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 sm:py-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/25 flex items-center justify-center">
              <Lightning size={16} weight="fill" className="text-[#FF5A1F]" />
            </div>
            <div>
              <div className="text-white text-sm sm:text-base font-semibold leading-snug">
                <span data-testid="pricing-strip-trial">7-day free trial</span>
                <span className="text-neutral-500 mx-2">→</span>
                <span data-testid="pricing-strip-solo" className="text-[#FF9764]">$79/mo Solo</span>
                <span className="text-neutral-600 mx-1.5">·</span>
                <span data-testid="pricing-strip-fleet" className="text-[#FF9764]">$299/mo Small Fleet</span>
              </div>
              <div className="text-xs text-neutral-400 mt-0.5">
                Card on file, $0 today. No per-load surcharges. Cancel anytime before day 8, keep your data 30 days.
              </div>
            </div>
          </div>
          <Link
            to="/pricing"
            data-testid="pricing-strip-see-plans"
            className="text-xs sm:text-sm text-[#FF5A1F] hover:text-[#FF7038] font-medium inline-flex items-center gap-1.5 whitespace-nowrap self-start sm:self-auto"
          >
            See full plans
            <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  BENEFIT BAR  ───────────────────── */

export function BenefitBar() {
  return (
    <section
      aria-label="Key benefits"
      data-testid="benefit-bar"
      className="border-b border-white/10 bg-black/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {BENEFITS.map((b) => (
            <div
              key={b.label}
              className="flex items-start gap-2.5 text-sm text-neutral-300"
            >
              <b.icon size={18} weight="bold" className="text-[#FF5A1F] mt-0.5 shrink-0" />
              <span className="leading-snug">{b.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  PROBLEM SECTION  ───────────────────── */

export function ProblemSection() {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="border-b border-white/10 py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="overline mb-3 text-[#FF9764]">The problem</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            Too much of your team's time is spent on{" "}
            <span className="text-[#FF5A1F]">repetitive work</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROBLEMS.map((p, i) => (
            <div
              key={p.t}
              data-testid={`problem-card-${i}`}
              className="rounded-xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/20 hover:bg-white/[0.04] transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/25 flex items-center justify-center shrink-0">
                  <p.icon size={16} weight="bold" className="text-[#FF5A1F]" />
                </div>
                <div className="text-neutral-200 text-sm sm:text-base leading-snug pt-1">{p.t}</div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-neutral-300 text-base sm:text-lg leading-relaxed mt-10 max-w-3xl">
          FreightPilotAI helps your team automate routine processes while keeping people in control
          of important decisions.
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────  SOLUTIONS SECTION  ───────────────────── */

export function SolutionsSection() {
  return (
    <section
      id="solutions"
      data-testid="solutions-section"
      className="border-b border-white/10 py-20 sm:py-28 scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="overline mb-3 text-[#FF9764]">Solutions</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            Practical AI, applied where it matters most.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {SOLUTIONS.map((s, i) => (
            <article
              key={s.title}
              data-testid={`solution-card-${i}`}
              className="group rounded-xl border border-white/10 bg-white/[0.02] p-6 hover:border-[#FF5A1F]/40 hover:bg-white/[0.04] transition-all"
            >
              <div className="w-11 h-11 rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/25 flex items-center justify-center mb-5">
                <s.icon size={20} weight="bold" className="text-[#FF5A1F]" />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2 leading-snug">{s.title}</h3>
              <p className="text-neutral-400 text-sm leading-relaxed mb-5">{s.body}</p>
              <Link
                to={s.to}
                data-testid={`solution-learn-${i}`}
                className="text-[#FF5A1F] hover:text-[#FF7038] text-sm font-medium inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all"
              >
                Learn More
                <ArrowRight size={14} weight="bold" />
              </Link>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  HOW IT WORKS  ───────────────────── */

export function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="border-b border-white/10 py-20 sm:py-28 scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="overline mb-3 text-[#FF9764]">How it works</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            A clear path from busy work to better work.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              data-testid={`step-card-${i}`}
              className="rounded-xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 relative overflow-hidden"
            >
              <div className="font-mono text-6xl sm:text-7xl font-semibold text-[#FF5A1F]/15 leading-none absolute top-4 right-5 select-none pointer-events-none">
                {s.n}
              </div>
              <div className="relative">
                <div className="overline text-[#FF9764] mb-3">Step {parseInt(s.n, 10)}</div>
                <h3 className="font-display text-2xl font-semibold mb-3">{s.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  TRUST SECTION  ───────────────────── */

export function TrustSection() {
  return (
    <section
      id="trust"
      data-testid="trust-section"
      className="border-b border-white/10 py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="overline mb-3 text-[#FF9764]">Responsible AI</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            Practical AI, implemented <span className="text-[#FF5A1F]">responsibly</span>.
          </h2>
          <p className="text-neutral-400 mt-4 text-sm sm:text-base leading-relaxed">
            Automation should support your team, not surprise them. Every workflow is designed
            with people, security, and predictability in mind.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TRUST_POINTS.map((p, i) => (
            <div
              key={p.t}
              data-testid={`trust-item-${i}`}
              className="rounded-xl border border-white/10 bg-white/[0.02] p-5 flex items-start gap-3"
            >
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
                <p.icon size={16} weight="bold" className="text-emerald-400" />
              </div>
              <div className="text-neutral-200 text-sm sm:text-base leading-snug pt-1">{p.t}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
